import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const agoShort = (d) => {
  if (!d) return '—';
  const mins = Math.floor((Date.now() - new Date(d)) / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const hoursSince = (d) => d ? (Date.now() - new Date(d)) / 3600000 : null;

const fmtNZT = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const ReplicationStatus = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('v_event_log_latest')
      .select('job_key, job_name, run_at_utc, records_fetched, records_upserted, duration_seconds, status, error_message')
      .eq('category', 'operational')
      .order('job_name', { ascending: true });
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <div className="dash-panel__header">
            <span className="dash-panel__title">⚡ Operational Events</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="action-btn action-btn--close"
                onClick={() => navigate('/data-sweep')}
              >
                🧹 Data Sweep
              </button>
              <button
                className="action-btn action-btn--close"
                onClick={() => navigate('/product-error-management')}
              >
                🔧 Product Error Management
              </button>
              <button className="dash-refresh-btn" onClick={load} disabled={loading}>↻</button>
            </div>
          </div>

          {loading ? <div className="dash-loading">Loading…</div> : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Feed</th>
                  <th className="num">Fetched</th>
                  <th className="num">Upserted</th>
                  <th>Last Run (NZT)</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Commentary</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // 25h, not 24: a daily job finishing a minute later than the
                  // day before would otherwise flash red until it completes.
                  const stale = (hoursSince(row.run_at_utc) ?? 0) > 25;
                  return (
                    <tr key={row.job_key} className={stale || row.status !== 'ok' ? 'dash-row--error' : ''}>
                      <td>{row.job_name}</td>
                      <td className="num">{row.records_fetched?.toLocaleString() ?? '—'}</td>
                      <td className="num">{row.records_upserted?.toLocaleString() ?? '—'}</td>
                      <td className="muted">{fmtNZT(row.run_at_utc)}</td>
                      <td className={stale ? undefined : 'muted'} style={stale ? { color: '#f87171', fontWeight: 600 } : undefined}>
                        {agoShort(row.run_at_utc)}
                      </td>
                      <td>
                        <span className={`status-dot status-dot--${row.status === 'ok' ? 'ok' : 'err'}`}>
                          {row.status === 'ok' ? '✓' : '✕'}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: row.status === 'ok' ? 'var(--green-light)' : '#f87171', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.error_message || ''}
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={7} className="dash-empty">No sync data found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

export default ReplicationStatus;
